import { useDraggable } from '../adapters/dndAdapter';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { getItemClass } from '../utils/dndClassNames';

/**
 * DraggableItem - Elemento arrastrable independiente (no para listas ordenables)
 *
 * Usa useDraggable de @dnd-kit internamente.
 * Útil para árboles y casos donde el elemento puede moverse a diferentes contenedores.
 *
 * Para listas ordenables simples, usar SortableItem en su lugar.
 *
 * @param {string|number} id - ID único del item
 * @param {React.ReactNode|Function} children - Contenido o render function
 * @param {boolean} [disabled=false] - Deshabilitar drag
 * @param {string} [className] - Clase CSS adicional
 * @param {Object} [data] - Datos adicionales para el drag
 *
 * Si children es función, recibe:
 * - isDragging: boolean
 * - attributes: objeto de atributos de accesibilidad
 * - listeners: event listeners para drag (aplicar al handle si se quiere separar)
 * - setNodeRef: ref para el elemento
 * - style: estilos de transformación
 * - transform: objeto transform raw (para personalización)
 */
const DraggableItem = ({ id, children, disabled = false, className, data }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: typeof id === 'number' ? String(id) : id,
    disabled,
    data,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  // Si children es función, pasarle el estado
  if (typeof children === 'function') {
    return children({
      isDragging,
      attributes,
      listeners,
      setNodeRef,
      style,
      transform,
    });
  }

  // Render por defecto
  return (
    <div ref={setNodeRef} style={style} className={clsx(className, getItemClass({ isDragging }))} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

DraggableItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  data: PropTypes.object,
};

export default DraggableItem;
